/** The parts of `ChatForUser` this needs — both the drawer screen and the home panel fetch them. */
interface ReadState {
    messages?: readonly { id: string; createdAt: string }[] | null;
    participants?: readonly { userId: string; latestViewedMessage?: { id: string } | null }[] | null;
}

/**
 * Whether `markChatRead` would actually change anything for this user.
 *
 * The mutation always advances the caller's `latestViewedMessageId` to the newest message in
 * the chat, so once that pointer is already the newest it is a pure no-op. Calling it anyway on
 * every open would mean a write plus an `unreadMessageCount` refetch each time, with nothing to
 * show for either — so callers gate on this first.
 *
 * `liveMessages` is the caller's locally-appended subscription messages. The `ChatForUser`
 * query result is only refetched on mount, so a message that arrived over AppSync since then
 * exists in the view but not in `chat.messages`; judged on `chat` alone the thread looks
 * already-read, nothing gets marked, and the unread badge (which *does* refetch on every live
 * event) stays lit over a chat the user is looking at.
 *
 * A participant who has never viewed anything has no pointer at all, which counts as unread as
 * long as the chat has a message to mark.
 */
export function hasUnreadToMark(
    chat: ReadState | null | undefined,
    userId: string | undefined,
    liveMessages: readonly { id: string; createdAt: string }[] = [],
): boolean {
    if (!chat || !userId) return false;
    // Sorted rather than trusting array order — the pointer must land on the true newest.
    const newest = [
        ...(chat.messages ?? []),
        ...liveMessages,
    ]
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
        .at(-1);
    if (!newest) return false;
    const viewedId = chat.participants?.find((p) => p.userId === userId)?.latestViewedMessage?.id;
    return newest.id !== viewedId;
}
